import { useEffect, useRef, useState } from "react";
import styles from "./Reveal.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface RevealProps {
  children: React.ReactNode;
  /** Stagger delay in ms — used to make siblings appear one-by-one. */
  delay?: number;
  /** Optional extra className on the wrapper. */
  className?: string;
  /** Render as this element (default div). */
  as?: keyof JSX.IntrinsicElements;
  /**
   * Keep the element hidden even once it has scrolled into view.
   *
   * Added for the List With Us hero, where the copy must wait for the globe to
   * finish its entrance rather than for a scroll position. Defaults to false, so
   * every existing caller behaves exactly as before.
   */
  hold?: boolean;
  /**
   * When the reveal is allowed to fire.
   *
   * `"early"` (the default, and what all 41 existing callers get) expands the
   * observer root a fifth of a screen PAST the fold, so the animation has
   * finished by the time the element is looked at. That is right for body copy
   * and cards, where a reader should never wait on a decorative transition.
   *
   * `"inview"` waits until the element is genuinely on screen. Use it for a
   * section heading, which a reader scrolls to deliberately — with `"early"` its
   * 0.7s transition is over before it arrives, and the heading reads as having no
   * entrance at all.
   */
  trigger?: "early" | "inview";
}

/**
 * Fade + rise-up scroll reveal. Animates once when the element first scrolls
 * into view, then stays visible (no replay on scroll-up). SSR-safe: renders in
 * the hidden state and reveals after hydration + intersection.
 *
 * `hold` lets a caller gate the reveal on something other than scroll position.
 */
const Reveal = ({
  children,
  delay = 0,
  className = "",
  as = "div",
  hold = false,
  trigger = "early",
}: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const Tag = as as any;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Respect reduced-motion: show immediately, skip the animation.
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return undefined;
    }

    // No IntersectionObserver (very old browser, or a JSDOM-style environment):
    // show the content. A decorative entrance animation must never be the reason
    // a page has no words on it.
    if (typeof IntersectionObserver !== "function") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect(); // once, then stay
          }
        });
      },
      /*
       * ⚠️  THRESHOLD 0 AND A POSITIVE BOTTOM MARGIN. Both halves matter, and the
       * previous values (`0.15` / `-10%`) fought each other on a short viewport.
       *
       * `rootMargin: "0px 0px -10% 0px"` SHRANK the trigger area to the top 90%
       * of the screen, and `threshold: 0.15` then required 15% of the ELEMENT to
       * be inside that band. For a section taller than the viewport — which is
       * most sections on a phone — that means the element is already well onto
       * the screen before it is told to appear. Measured on a 390px viewport,
       * eight of the ten reveal wrappers in the first two screens of About Us
       * were still at opacity 0 after the page had fully settled: the reader got
       * a heading and then white space, exactly as reported.
       *
       * Inverted:
       *   threshold 0   — fire as soon as ANY part of the element is in range,
       *                   regardless of how tall the element is.
       *   +20% bottom   — EXPAND the root a fifth of a screen PAST the fold, so
       *                   the reveal starts before the element is scrolled to and
       *                   has finished by the time it is looked at.
       *
       * Net effect: content in or near the first screen is visible on load, and
       * everything below still animates in — just early enough that the animation
       * is something you notice rather than something you wait for.
       */
      //
      // `trigger: "inview"` opts out of the +20%: a NEGATIVE bottom margin pulls
      // the trigger line up off the fold instead, so nothing fires until the
      // element is properly on screen and the transition plays where it can be
      // seen. Threshold stays 0 so a section taller than the viewport still
      // fires on its top edge rather than waiting to be a quarter scrolled past.
      trigger === "inview"
        ? { threshold: 0, rootMargin: "0px 0px -12% 0px" }
        : { threshold: 0, rootMargin: "0px 0px 20% 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [trigger]);

  return (
    <Tag
      ref={ref}
      className={`${styles.reveal} ${visible && !hold ? styles.isVisible : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
};

export default wrapperHOC(Reveal, {
  componentName: "Reveal",
  showForChina: true,
});
